import AttributeBundle from '../styles/AttributeBundle';
import StyleAttribute from '../styles/StyleAttribute';
import FontStyle from '../styles/FontStyle';
import FontWeight from '../styles/FontWeight';
import TextAlignment from '../styles/TextAlignment';
import TextDecoration from '../styles/TextDecoration';
import Dimension from './Dimension';
import DimensionUnit from './DimensionUnit';
/**
 * Returns the function name for determining if an attribute has an override.
 * @param {StyleChain|AttributeBundle} styleChain
 * @returns {string} hasOverride function name
 * @private
 */
function _getHasStyleFunction(styleChain, allowBaseStyles) {
    var funcName;
    if (allowBaseStyles) {
        funcName = 'hasStyleAttributeValue';
    }
    else if (styleChain instanceof AttributeBundle) {
        funcName = 'hasOverrideForStyleAttribute';
    }
    else {
        funcName = 'hasStyleAttributeValueAboveBase';
    }
    // FIXME:
    // tslint:disable-next-line:no-unsafe-any ban
    return styleChain[funcName].bind(styleChain);
}
/**
 * Gets a color string in rgba format for a given color attribute.
 * @param {StyleChain|AttributeBundle} styleAttrs
 * @param {AttributeName} attributeColor
 * @param {number=} defaultColor unsigned defaultColor
 * @returns {string} rgba color string
 * @private
 */
function _getColorStringFromStyles(styleAttrs, attributeColor, defaultColor, allowBaseStyles) {
    if (styleAttrs) {
        var hasOverrideFunction = _getHasStyleFunction(styleAttrs, allowBaseStyles);
        var alpha = 1;
        //
        // Check if there is a transparency override.
        //
        // If we're looking for FILL_COLOR then check FILL_TRANSPARENCY first.
        // The spec defines TIP_FILL to use fill-color and fill-transparency
        // but VAD may send transparency so also check TRANSPARENCY.
        //
        if (attributeColor === StyleAttribute.FILL_COLOR && hasOverrideFunction(StyleAttribute.FILL_TRANSPARENCY)) {
            alpha = 1 - styleAttrs.getStyleAttributeFloatValue(StyleAttribute.FILL_TRANSPARENCY, 1);
        }
        else if (hasOverrideFunction(StyleAttribute.TRANSPARENCY)) {
            alpha = 1 - styleAttrs.getStyleAttributeFloatValue(StyleAttribute.TRANSPARENCY, 1);
        }
        if (hasOverrideFunction(attributeColor)) {
            // NOTE(mikcol): legacy did not pass a 2nd parm
            var attrColor = styleAttrs.getStyleAttributeUnsignedValue(attributeColor, 0);
            return getRgbaColorString(attrColor, alpha);
        }
        else if (defaultColor !== undefined) {
            return getRgbaColorString(defaultColor, alpha);
        }
    }
    return '';
}
/**
 * Returns a color string for the specified style by looking through to
 * the base theme.
 * @param {StyleChain|AttributeBundle} styleChain
 * @param {StyleAttribute} attributeName
 * @param {number} alpha
 * @returns {string} A string representation of the converted css <color> value.
 */
export function getStyleColorString(styleChain, attributeName, alpha) {
    var unsignedColor = styleChain.getStyleAttributeUnsignedValue(attributeName, 0);
    return getRgbaColorString(unsignedColor, alpha);
}
/**
 * Returns a rgba color string that accounts for transparency.
 * NOTE: Transparency is typically needed for background color
 *
 * @param {StyleChain|AttributeBundle} styleAttrs
 * @param {StyleAttribute} attributeColor
 * @param {number} defaultColor Integer representation of the default color value (e.g. 0xFFFFFF)
 * @returns {string} A string representation of the converted css <color> value.
 */
export function getColorStringForAttributeColor(styleAttrs, attributeColor, defaultColor, enableBaseStyles) {
    return _getColorStringFromStyles(styleAttrs, attributeColor, defaultColor, enableBaseStyles);
}
export function getRgbaColorString(color, alpha) {
    /*eslint-disable no-bitwise */
    var r = (color >> 16) & 255;
    var g = (color >> 8) & 255;
    var b = color & 255;
    return 'rgba(' + [r, g, b, alpha].join() + ')';
    /*eslint-enable */
}
/**
 * Returns the background color as an rgba color string.
 * @param {StyleChain|AttributeBundle} styleAttrs
 * @returns {string} A string representation of the converted css <color> value.
 */
export function getBackgroundColorString(styleAttrs) {
    return _getColorStringFromStyles(styleAttrs, StyleAttribute.BACKGROUND_COLOR);
}
/**
 * Using the provided StyleChain the styles relevent to border styling are converted into valid css style mappings.
 * @param {StyleChain} styleChain
 * @param {Object=} css Existing styles bundle to append styles to
 * @returns {Object} An object bundle with border styles.
 */
export function getAllBorderStyles(styleChain, css, allowBaseStyles) {
    var hasOverrideFunction = _getHasStyleFunction(styleChain, allowBaseStyles);
    if (hasOverrideFunction(StyleAttribute.BORDER_COLOR)) {
        css = css || {};
        css['border-color'] = getStyleColorString(styleChain, StyleAttribute.BORDER_COLOR, 1);
    }
    if (hasOverrideFunction(StyleAttribute.BORDER_WIDTH)) {
        var borderDim = styleChain.getStyleAttributeDimensionValue(StyleAttribute.BORDER_WIDTH, new Dimension(1, DimensionUnit.PIXELS));
        css = css || {};
        css['border-width'] = borderDim
            ? String(borderDim.getScalarValueForUnit(DimensionUnit.PIXELS, undefined, undefined)) + 'px'
            : '0px';
        if (css['border-width']) {
            css['border-style'] = 'solid';
        }
    }
    return css;
}
/**
 * Using the provided StyleChain the styles relevent to text styling are converted into valid css style mappings.
 * @param {StyleChain} styleChain
 * @returns {Object} An object bundle containing the letious styles related to text stying,
 */
export function getAllTextStyles(styleChain, allowBaseStyles) {
    if (!styleChain) {
        return null;
    }
    var styles;
    var hasOverrideFunction = _getHasStyleFunction(styleChain, allowBaseStyles);
    if (hasOverrideFunction(StyleAttribute.BACKGROUND_COLOR)) {
        styles = styles || {};
        //Some classes use text styles more generically then they should (PromptContainer).
        //  If transparency is needed for text background color then this would need to be rectified. For now assume alpha chanel === 1.
        styles['background-color'] = getRgbaColorString(styleChain.getStyleAttributeUnsignedValue(StyleAttribute.BACKGROUND_COLOR, 0), 1);
    }
    if (hasOverrideFunction(StyleAttribute.FONT_FAMILY)) {
        styles = styles || {};
        styles['font-family'] = styleChain.getStyleAttributeStringValue(StyleAttribute.FONT_FAMILY, 'Arial');
    }
    if (hasOverrideFunction(StyleAttribute.COLOR)) {
        styles = styles || {};
        styles.color = getStyleColorString(styleChain, StyleAttribute.COLOR, 1);
    }
    if (hasOverrideFunction(StyleAttribute.TEXT_COLOR)) {
        styles = styles || {};
        styles.color = getStyleColorString(styleChain, StyleAttribute.TEXT_COLOR, 1);
    }
    if (hasOverrideFunction(StyleAttribute.FONT_SIZE)) {
        styles = styles || {};
        styles['font-size'] = getFontSize(styleChain);
    }
    if (hasOverrideFunction(StyleAttribute.FONT_STYLE)) {
        styles = styles || {};
        styles['font-style'] = getFontStyle(styleChain);
    }
    if (hasOverrideFunction(StyleAttribute.FONT_WEIGHT)) {
        styles = styles || {};
        styles['font-weight'] = getFontWeight(styleChain);
    }
    if (hasOverrideFunction(StyleAttribute.TEXT_ALIGNMENT)) {
        styles = styles || {};
        styles['text-align'] = getTextAlignment(styleChain);
    }
    if (hasOverrideFunction(StyleAttribute.TEXT_DECORATION)) {
        styles = styles || {};
        styles['text-decoration'] = getTextDecoration(styleChain);
    }
    return styles;
}
/**********************************************************************/
/*****                 Begin font helper functions                    ****/
/**********************************************************************/
export function getFontSize(styleChain) {
    if (!styleChain) {
        return null;
    }
    var sizeDimension = styleChain.getStyleAttributeDimensionValue(StyleAttribute.FONT_SIZE, new Dimension(12, DimensionUnit.POINTS));
    if (sizeDimension) {
        // TODO: Check this logic?  How was this ever working without passing the 2nd parameter.
        // DPI of -1 means use the dpi from resource manager.
        return String(sizeDimension.getScalarValueForUnit(DimensionUnit.POINTS, -1, 0)) + 'pt';
    }
    else {
        return '0pt';
    }
}
export function getFontWeight(styleChain) {
    if (!styleChain) {
        return null;
    }
    var font = styleChain.getStyleAttributeUnsignedValue(StyleAttribute.FONT_WEIGHT, FontWeight.NORMAL);
    if (font === FontWeight.BOLD) {
        return 'bold';
    }
    else {
        return 'normal';
    }
}
export function getFontStyle(styleChain) {
    if (!styleChain) {
        return null;
    }
    var fontStyle = styleChain.getStyleAttributeUnsignedValue(StyleAttribute.FONT_STYLE, FontStyle.NORMAL);
    if (fontStyle === FontStyle.ITALIC) {
        return 'italic';
    }
    else {
        return 'normal';
    }
}
export function getTextAlignment(styleChain) {
    if (!styleChain) {
        return null;
    }
    var alignment = styleChain.getStyleAttributeUnsignedValue(StyleAttribute.TEXT_ALIGNMENT, TextAlignment.LEFT);
    var textAlign = 'left';
    if (alignment === TextAlignment.CENTER) {
        textAlign = 'center';
    }
    else if (alignment === TextAlignment.RIGHT) {
        textAlign = 'right';
    }
    return textAlign;
}
export function getTextDecoration(styleChain) {
    if (!styleChain) {
        return null;
    }
    var textDecoration = styleChain.getStyleAttributeUnsignedValue(StyleAttribute.TEXT_DECORATION, TextDecoration.NONE);
    var decoration = 'none';
    if (textDecoration === TextDecoration.UNDERLINE) {
        decoration = 'underline';
    }
    return decoration;
}
